<script setup lang="ts">
import { computed, provide, onMounted } from 'vue';
import { Offcanvas } from 'bootstrap';
import { useNavigation } from '../composables/useNavigation';
import { useCollapseState } from '../composables/useCollapseState';
import NavBarItem from './NavBarItem.vue';
import type { NavGroup } from '@/types/routing-types';

const props = defineProps<{
  isMenuCollapsed: boolean;
}>();

const { menuItems } = useNavigation();
const { isExpanded, toggleCollapse, autoExpandFromRoute } = useCollapseState(menuItems);

// Visible groups (filter out empty groups)
const visibleGroups = computed(() => {
  const groups: NavGroup[] = ['main', 'publisher', 'partners', 'admin', 'dev'];
  return groups.filter(group => {
    const items = getItemsForGroup(group);
    return items.length > 0;
  });
});

// Get menu items for a specific group
function getItemsForGroup(group: NavGroup) {
  return menuItems.value.filter(item => item.group === group);
}

// Format group name for display
function formatGroupName(group: NavGroup): string {
  if (props.isMenuCollapsed) {
    return group === 'publisher' ? 'pub' :
           group === 'partners' ? 'ptnr' : group;
  }
  return group === 'partners' ? 'partner' : group;
}

// Close mobile offcanvas
function closeOffcanvas() {
  const element = document.getElementById('sidebarMenu');
  if (element) {
    const offcanvas = Offcanvas.getInstance(element) || new Offcanvas(element);
    offcanvas.hide();
  }
}

// Provide functions to child components
provide('closeOffcanvas', closeOffcanvas);
provide('collapseState', { isExpanded, toggleCollapse });

// Setup — data loading is driven reactively by the watch in useNavigation
// (useProjects/useProducts queries auto-fetch and trigger loadAllDynamicChildren)
onMounted(() => {
  autoExpandFromRoute();
});
</script>

<template>
  <!-- Group by navigation groups -->
  <ul
    v-for="group in visibleGroups"
    :key="group"
    class="navbar-nav flex-column"
    :class="{ 'text-center': isMenuCollapsed }"
  >
    <!-- Group header -->
    <li
      v-if="group !== 'main'"
      class="nav-link text-secondary text-uppercase"
      :class="{ 'text-center': isMenuCollapsed }"
    >
      {{ formatGroupName(group) }}
    </li>

    <!-- Menu items for this group -->
    <nav-bar-item
      v-for="item in getItemsForGroup(group)"
      :key="item.path"
      :item="item"
      :is-collapsed="isMenuCollapsed"
    />
</ul>
</template>

<style lang="scss" scoped>
.nav-link.text-secondary.text-uppercase {
  font-size: 0.9em;
}
</style>
